/**
 * Utilidades comunes para manejo de dispositivos
 */

/**
 * Obtiene el emoji apropiado según el tipo de dispositivo
 * @param {string} userType - Tipo de usuario (conductor, cliente, etc)
 * @param {string} platform - Plataforma del dispositivo
 * @param {string} userAgent - User agent del navegador
 * @returns {string} Emoji representativo del dispositivo
 */
export const getDeviceEmoji = (userType, platform, userAgent) => {
  // Si es conductor, siempre taxi
  if (userType === 'conductor') {
    return '🚕';
  }
  
  return getDeviceHardwareEmoji(platform, userAgent);
};

/**
 * Obtiene el emoji del hardware del dispositivo (sin considerar user_type)
 * @param {string} platform - Plataforma del dispositivo
 * @param {string} userAgent - User agent del navegador
 * @returns {string} Emoji representativo del hardware
 */
export const getDeviceHardwareEmoji = (platform, userAgent) => {
  const ua = (userAgent || '').toLowerCase();
  const plat = (platform || '').toLowerCase();
  
  // Detectar iOS/Apple
  if (plat.includes('ios') || plat.includes('iphone') || plat.includes('ipad') || 
      ua.includes('iphone') || ua.includes('ipad') || ua.includes('macintosh') || ua.includes('mac os')) {
    return '🍎';
  }
  
  // Detectar Android
  if (plat.includes('android') || ua.includes('android')) {
    return '🤖';
  }
  
  // Detectar PC/Desktop (Windows, Linux)
  if (plat.includes('win') || plat.includes('linux') || 
      ua.includes('windows') || ua.includes('linux') || 
      (!ua.includes('mobile') && (ua.includes('mozilla') || ua.includes('chrome')))) {
    return '💻';
  }
  
  // Desconocido
  return '👤';
};

/**
 * Determina si un dispositivo está activo basándose en su last_seen
 * @param {string|Date} lastSeen - Fecha de última actividad
 * @param {number} minutesThreshold - Minutos para considerar activo (default: 5)
 * @returns {boolean} true si está activo
 */
export const isDeviceActive = (lastSeen, minutesThreshold = 5) => {
  if (!lastSeen) return false;
  
  const lastSeenDate = new Date(lastSeen);
  const now = new Date();
  const diffMinutes = (now - lastSeenDate) / (1000 * 60);
  
  return diffMinutes <= minutesThreshold;
};

/**
 * Obtiene el estado visual de un dispositivo
 * @param {string|Date} lastSeen - Fecha de última actividad
 * @returns {object} { color, label, emoji }
 */
export const getDeviceStatus = (lastSeen) => {
  if (!lastSeen) {
    return { color: 'gray', label: 'Sin actividad', emoji: '⚪' };
  }
  
  const lastSeenDate = new Date(lastSeen);
  const now = new Date();
  const diffMinutes = (now - lastSeenDate) / (1000 * 60);
  
  if (diffMinutes <= 5) {
    return { color: 'green', label: 'Activo', emoji: '🟢' };
  } else if (diffMinutes <= 60) {
    return { color: 'yellow', label: 'Reciente', emoji: '🟡' };
  } else {
    return { color: 'red', label: 'Inactivo', emoji: '🔴' };
  }
};

/**
 * Formatea la fecha de last_seen
 * @param {string|Date} lastSeen - Fecha a formatear
 * @returns {string} Fecha formateada o 'Sin fecha'
 */
export const formatLastSeen = (lastSeen) => {
  if (!lastSeen) return 'Sin fecha';
  
  return new Date(lastSeen).toLocaleString('es-ES', { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit', 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};
